import React, { useEffect, useState } from "react";
import { Users, Wallet, Palmtree, Ticket, Briefcase, Receipt, Play, Download, Save, Trash2, Plus, X, FileBarChart, Clock, Send } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Loading, Modal, Spinner, Badge, EmptyState, fmtMoney } from "../components/ui";

const C = ["#2563EB", "#16A34A", "#D97706", "#DC2626", "#7C3AED", "#0891B2"];
const OPS = [["contains", "contains"], ["eq", "equals"], ["neq", "not equals"], ["gt", ">"], ["lt", "<"]];

function postBlob(path, payload, filename) {
  return api.post(path, payload, { responseType: "blob" }).then((res) => {
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
  });
}

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports").then(({ data }) => setD(data)); }, []);
  if (!d) return <Loading />;
  const stats = [
    [Users, "Headcount", d.headcount, "bg-accent/10 text-accent"],
    [Wallet, "Monthly Payroll", fmtMoney(d.monthlyPayroll), "bg-emerald-500/10 text-emerald-600"],
    [Palmtree, "Pending Leave", d.pendingLeave, "bg-amber-500/10 text-amber-600"],
    [Receipt, "Open Expenses", d.openExpenses, "bg-violet-500/10 text-violet-600"],
    [Ticket, "Open Tickets", d.openTickets, "bg-rose-500/10 text-rose-600"],
    [Briefcase, "Open Positions", d.openPositions, "bg-sky-500/10 text-sky-600"],
  ];
  return (
    <div>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
        {stats.map(([Ic, l, v, a]) => (
          <Card key={l} className="p-5" data-testid={`report-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${a}`}><Ic size={19} /></div>
            <p className="mt-3 font-head text-2xl font-extrabold text-slate-900 dark:text-white">{v}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">{l}</p>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {[["Headcount by Department", d.byDepartment], ["Headcount by Location", d.byLocation]].map(([title, data]) => (
          <Card key={title} className="p-6">
            <h3 className="mb-4 font-head text-lg font-bold text-slate-900 dark:text-white">{title}</h3>
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "rgba(37,99,235,0.06)" }} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={50}>{data.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Builder({ preset, sources }) {
  const [source, setSource] = useState(preset?.source || "");
  const [fields, setFields] = useState(preset?.fields || []);
  const [filters, setFilters] = useState(preset?.filters || []);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: "", scheduleOn: false, frequency: "Monthly", recipients: "" });

  const srcDef = sources.find((s) => s.key === source);
  useEffect(() => { if (preset) { setSource(preset.source); setFields(preset.fields); setFilters(preset.filters || []); setResult(null); } }, [preset]);

  const pickSource = (k) => { setSource(k); setFields([]); setFilters([]); setResult(null); };
  const toggleField = (n) => setFields((f) => f.includes(n) ? f.filter((x) => x !== n) : [...f, n]);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/reports/builder/run", { source, fields, filters }); setResult(data); }
    catch (e) { alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const exportCsv = () => postBlob("/reports/builder/export", { source, fields, filters }, `report-${source}.csv`).catch(() => alert("Export failed"));
  const save = async () => {
    if (!saveForm.name) return alert("Give the report a name.");
    try {
      await api.post("/reports/saved", { name: saveForm.name, source, fields, filters,
        schedule: saveForm.scheduleOn ? { frequency: saveForm.frequency, recipients: saveForm.recipients } : null });
      setSaveOpen(false); setSaveForm({ name: "", scheduleOn: false, frequency: "Monthly", recipients: "" });
      alert("Report saved");
    } catch (e) { alert(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
      <Card className="p-5">
        <label className="lbl mb-1 block">Data Source</label>
        <select data-testid="rb-source" value={source} onChange={(e) => pickSource(e.target.value)} className="input">
          <option value="">— Select —</option>
          {sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        {srcDef && (
          <>
            <p className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Columns</p>
            <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1" data-testid="rb-fields">
              {srcDef.fields.map((f) => (
                <label key={f.name} className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                  <input type="checkbox" data-testid={`rb-field-${f.name}`} checked={fields.includes(f.name)} onChange={() => toggleField(f.name)} className="accent-accent" /> {f.label}
                </label>
              ))}
            </div>
            <button data-testid="rb-run" disabled={busy} onClick={run} className="btn-accent mt-5 flex w-full items-center justify-center gap-1.5">{busy ? <Spinner className="text-white" /> : <><Play size={15} /> Run Report</>}</button>
          </>
        )}
      </Card>

      <div>
        {srcDef && (
          <Card className="mb-4 p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Filters</span>
              <button data-testid="rb-add-filter" onClick={() => setFilters((f) => [...f, { field: srcDef.fields[0].name, op: "contains", value: "" }])} className="flex items-center gap-1 text-xs font-semibold text-accent hover:underline"><Plus size={13} /> Add filter</button>
            </div>
            {filters.length === 0 ? <p className="text-xs text-slate-400">No filters — all rows returned.</p> : (
              <div className="space-y-2" data-testid="rb-filters">
                {filters.map((flt, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2">
                    <select value={flt.field} onChange={(e) => setFilters((fs) => fs.map((x, j) => j === i ? { ...x, field: e.target.value } : x))} className="input w-auto py-1.5 text-sm">{srcDef.fields.map((f) => <option key={f.name} value={f.name}>{f.label}</option>)}</select>
                    <select value={flt.op} onChange={(e) => setFilters((fs) => fs.map((x, j) => j === i ? { ...x, op: e.target.value } : x))} className="input w-auto py-1.5 text-sm">{OPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                    <input value={flt.value} onChange={(e) => setFilters((fs) => fs.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} className="input w-32 py-1.5 text-sm" placeholder="value" />
                    <button onClick={() => setFilters((fs) => fs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><X size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {!result ? <EmptyState icon={FileBarChart} title="Build a report" hint="Pick a data source, choose columns, optionally add filters, then Run." /> : (
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200" data-testid="rb-result-count">{result.total} rows</span>
              <div className="flex gap-2">
                <button data-testid="rb-export" onClick={exportCsv} className="btn-outline flex items-center gap-1.5 px-3 py-1.5 text-sm"><Download size={14} /> Export CSV</button>
                <button data-testid="rb-save" onClick={() => setSaveOpen(true)} className="btn-accent flex items-center gap-1.5 px-3 py-1.5 text-sm"><Save size={14} /> Save</button>
              </div>
            </div>
            <div className="max-h-[55vh] overflow-auto">
              <table className="w-full text-sm" data-testid="rb-result-table">
                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800"><tr>{result.columns.map((c) => <th key={c.name} className="th">{c.label}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {result.rows.map((r, i) => <tr key={i}>{result.columns.map((c) => <td key={c.name} className="td">{String(r[c.name] ?? "")}</td>)}</tr>)}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      <Modal open={saveOpen} onClose={() => setSaveOpen(false)} title="Save Report"
        footer={<><button onClick={() => setSaveOpen(false)} className="btn-outline">Cancel</button><button data-testid="rb-save-submit" onClick={save} className="btn-accent">Save Report</button></>}>
        <div className="space-y-4">
          <div><label className="label">Report Name</label><input data-testid="rb-save-name" value={saveForm.name} onChange={(e) => setSaveForm((s) => ({ ...s, name: e.target.value }))} className="input" placeholder="e.g. Monthly Net Pay Summary" /></div>
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium"><input type="checkbox" data-testid="rb-schedule-toggle" checked={saveForm.scheduleOn} onChange={(e) => setSaveForm((s) => ({ ...s, scheduleOn: e.target.checked }))} className="accent-accent" /> Schedule email delivery</label>
          {saveForm.scheduleOn && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="lbl mb-1 block">Frequency</label><select value={saveForm.frequency} onChange={(e) => setSaveForm((s) => ({ ...s, frequency: e.target.value }))} className="input">{["Daily", "Weekly", "Monthly"].map((f) => <option key={f}>{f}</option>)}</select></div>
              <div><label className="lbl mb-1 block">Recipients</label><input value={saveForm.recipients} onChange={(e) => setSaveForm((s) => ({ ...s, recipients: e.target.value }))} className="input" placeholder="hr@smifs.com" /></div>
              <p className="col-span-2 text-xs text-amber-600">Scheduling is configured & stored. Automated email delivery activates once the email integration is enabled.</p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

function Saved({ sources, onLoad }) {
  const [list, setList] = useState(null);
  const load = () => api.get("/reports/saved").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (window.confirm("Delete this saved report?")) { await api.delete(`/reports/saved/${id}`); load(); } };
  const send = async (id) => {
    try { const { data } = await api.post(`/reports/saved/${id}/send`); window.alert(`Sent to ${data.recipients} recipient(s) · ${data.rows} rows. (MOCK — see Email Outbox)`); }
    catch (e) { window.alert(apiError(e.response?.data?.detail)); }
  };
  if (!list) return <Loading />;
  if (list.length === 0) return <EmptyState icon={Save} title="No saved reports" hint="Build a report and click Save to reuse it later." />;
  return (
    <div className="grid gap-3" data-testid="saved-list">
      {list.map((r) => (
        <Card key={r.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`saved-${r.id}`}>
          <div>
            <p className="font-semibold text-slate-800 dark:text-slate-100">{r.name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{sources.find((s) => s.key === r.source)?.label || r.source} · {r.fields.length} columns · {(r.filters || []).length} filters{r.schedule ? ` · 📧 ${r.schedule.frequency}` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            {r.schedule && <Badge tone="info"><Clock size={11} className="mr-0.5 inline" />{r.schedule.frequency}</Badge>}
            {r.schedule && <button data-testid={`saved-send-${r.id}`} onClick={() => send(r.id)} className="btn-outline px-3 py-1.5 text-sm"><Send size={13} className="mr-1 inline" />Send Now</button>}
            <button data-testid={`saved-run-${r.id}`} onClick={() => onLoad(r)} className="btn-outline px-3 py-1.5 text-sm"><Play size={13} className="mr-1 inline" />Open</button>
            <button data-testid={`saved-del-${r.id}`} onClick={() => del(r.id)} className="rounded-md p-2 text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
          </div>
        </Card>
      ))}
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const canBuild = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);
  const [tab, setTab] = useState("dashboard");
  const [sources, setSources] = useState([]);
  const [preset, setPreset] = useState(null);
  useEffect(() => { if (canBuild) api.get("/reports/builder/sources").then(({ data }) => setSources(data)).catch(() => {}); }, [canBuild]);

  const tabs = [["dashboard", "Dashboard"]];
  if (canBuild) tabs.push(["builder", "Report Builder"], ["saved", "Saved Reports"]);

  return (
    <div>
      <PageHeader title="Reports" subtitle={canBuild ? "Analytics dashboard · field-pick report builder · scheduled reports" : "Organisation-wide analytics & insights"} />
      <div className="mb-6 flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => { setTab(k); if (k !== "builder") setPreset(null); }} className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-accent text-accent" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>{l}</button>
        ))}
      </div>
      {tab === "dashboard" && <Dashboard />}
      {tab === "builder" && canBuild && <Builder preset={preset} sources={sources} />}
      {tab === "saved" && canBuild && <Saved sources={sources} onLoad={(r) => { setPreset(r); setTab("builder"); }} />}
    </div>
  );
}
